import React, { useMemo } from 'react';

// No TooltipIcon any more: the standalone further-information branch it rendered is now one line in
// the needs-attention popover's message list, so the state is named in words rather than encoded in
// a fourth glyph the reader has to learn.
import Icon, { faCheck, faInfoCircle, faMinus, faTriangleExclamation, faXmark } from '~components/icon';

import { requiresLateSubmissionReason } from '~support-at-home/lib/lateSubmission';
import { parseSahApiErrors } from '~support-at-home/lib/sahApiError';

import { ClaimItem } from '~support-at-home/models';

import HoverPopover from '~support-at-home/components/common/HoverPopover';
import ClaimItemRequirements from './ClaimItemRequirements';

interface ComponentProps {
  id: string;
  // `ClaimItem`, not the `any` the sibling ClaimValidIcon uses: all the `sa_*` fields read below
  // are brand new on the model, and typing the prop is what makes a later rename of one of them
  // (TAV-2593 onwards) a build error here instead of a silent fall-back to the "not pushed" icon.
  // Narrowing is safe for the table config - `Column.component` is `FunctionComponent<any>`.
  row: ClaimItem;
}

/**
 * ClaimSahPushIcon
 *
 * Per-row status icon for the claim items table's `sa_status` column (TAV-2590), modelled on
 * ClaimValidIcon. Only rendered when the claim has a Services Australia invoice (the column's
 * `showColumn` in claimItemDetailsList.ts hides it entirely for non-API providers).
 *
 * The cell composes TWO things side by side:
 *
 *  1. An outstanding-flags warning triangle (TAV-2592), when Services Australia is waiting on
 *     something for this item. Hovering it lists exactly what. This sits OUTSIDE the priority chain
 *     below deliberately - the chain returns early, so a push error used to swallow the attachment
 *     and late-submission states entirely, and those are precisely what the provider has to fix
 *     while the item is erroring.
 *  2. The status indicator itself, whose priority order mirrors what a provider needs to act on
 *     first: a failed push, then attachment/further-information flags, then plain pushed/not-pushed.
 *     <ClaimItemRequirements> is always rendered alongside it - it owns its own `faPlus` trigger and
 *     tabbed attachments / late-submission-reason modal, and renders nothing itself when neither
 *     applies to this item (TAV-2592, previously two separate triggers - TAV-2591/2592).
 *
 * The failure state deliberately splits the status from its explanation: a plain red X states the
 * outcome, and a separate small info button beside it opens a PopOver with the message. Making the
 * user hover an X to discover why is not a discoverable affordance, whereas an info icon advertises
 * that there is something to read.
 *
 * A PopOver rather than a TooltipIcon for both popovers, because a vendor validation message is a
 * sentence (sometimes several) and can wrap over multiple lines - it needs real padding, a header
 * and a width bound to stay legible, which a tooltip does not give. Both share
 * <HoverPopover> so they cannot drift apart; they differ only by colour, glyph, header and
 * message list. The short, static states below keep using TooltipIcon, the right weight for a fixed
 * one-line hint.
 *
 * Only the human-readable part of the error is shown (parseSahApiErrors); the full string - status
 * code, request path, raw JSON body - is kept verbatim in `sa_push_error` for diagnosis.
 */
const ClaimSahPushIcon: React.FC<ComponentProps> = ({ id, row }) => {
  const errorMessages = useMemo(() => parseSahApiErrors(row.sa_push_error), [row.sa_push_error]);

  // What Services Australia is still waiting on for this item, one sentence per outstanding flag.
  // Each entry is a REQUIREMENT, not a status: satisfied flags drop out of the list, so an empty
  // array means nothing is outstanding and <HoverPopover> renders nothing at all.
  const outstandingFlags = useMemo(() => {
    // A push error suppresses this entirely. The two are not peers: an error means Services
    // Australia rejected the item outright, so "what it still needs" is not yet a meaningful
    // question - showing both would ask the provider to act on requirements for a row that never
    // landed. The error is the thing to fix first, and its own popover says so.
    if (errorMessages.length > 0) return [];

    const flags: string[] = [];

    // required && !provided - the same test the banner count and the drill-down filter use.
    // Services Australia may also flip attachmentRequired back to false once evidence lands, so
    // keying on the requirement alone would drop the row out of this list either way.
    if (row.sa_attachment_required && !row.sa_attachment_provided) {
      flags.push('An attachment is required as evidence for this item.');
    }

    // Shared predicate with the API and the late-submission column, so this line cannot disagree
    // with the warning triangle in that column or with items_late_submission_required.
    if (requiresLateSubmissionReason(row) && !row.late_submission_reason_code) {
      flags.push('A late submission reason is required before this invoice can be submitted to Services Australia.');
    }

    if (row.sa_further_information_required) {
      flags.push('Services Australia requires further information for this item.');
    }

    return flags;
    // The whole row, not the individual fields: requiresLateSubmissionReason takes a ClaimItem, so
    // listing its inputs separately alongside `row` is redundant without being any safer.
  }, [row, errorMessages]);

  const indicator = useMemo(() => {
    if (errorMessages.length > 0) {
      return (
        <div className="d-flex align-items-center justify-content-center gap-2">
          <Icon icon={faXmark} color="red" />
          <HoverPopover
            id={id}
            icon={faInfoCircle}
            variant="danger"
            // Blue, not red: the red X beside it already states the outcome, and this advertises
            // that there is something to READ. Two reds would just be one louder failure.
            triggerClassName="text-primary"
            header="Services Australia error"
            messages={errorMessages}
            ariaLabel="Show the Services Australia error for this item"
            dataCyPrefix="claim-sah-push-error"
          />
        </div>
      );
    }
    // The needs-attention triangle takes the STATUS GLYPH slot - it replaces the tick/minus rather
    // than sitting beside it. It is the more urgent reading of the row (Services Australia is
    // waiting on something), and it carries the explanation, which a bare tick cannot.
    if (outstandingFlags.length > 0) {
      return (
        <HoverPopover
          id={id}
          icon={faTriangleExclamation}
          variant="warning"
          triggerClassName="text-warning"
          // Explicit rather than inherited: `text-warning` is Bootstrap's yellow, and this triangle
          // has to read as the same orange as the other needs-attention icons in the table.
          iconColor="orange"
          header="Action required"
          messages={outstandingFlags}
          ariaLabel="Show what Services Australia requires for this item"
          dataCyPrefix="claim-sah-push-warning"
        />
      );
    }
    if (row.sa_invoice_item_id) {
      return <Icon icon={faCheck} color="green" />;
    }
    return <Icon icon={faMinus} />;
  }, [id, errorMessages, outstandingFlags, row.sa_invoice_item_id]);

  return (
    <div className="d-flex justify-content-center align-items-center gap-2" id={`claim-sah-push-cell-${id}`}>
      {indicator}
      {/*
        ClaimItemRequirements is the ACTION and is rendered independently of whatever glyph the
        status slot resolved to - the two answer different questions ("what is outstanding" vs "how
        do I fix it"), so an erroring or flagged row must still be able to reach its requirements.
        It renders nothing itself when neither an attachment nor a late submission reason applies to
        this item, so it is always safe to render here unconditionally (TAV-2592).
      */}
      <ClaimItemRequirements itemId={id} claimId={row.claim?.id} row={row} />
    </div>
  );
};

export default ClaimSahPushIcon;
